/**
 * One exchange, as either rep surface shows it. S8.3, S8.7 and S8.8.
 *
 * Shared, because the widget and the standalone application answer the same question about the same thing and
 * differ only in how they were given a credential. The widget is scoped to this one exchange by its token; the
 * standalone application navigated here from its directory. Neither difference is visible below, which is the
 * property that keeps one file honest instead of two that drift.
 *
 * ## The layout
 *
 * Four tabs and a queue underneath them, since DG.41. The two columns this used to promote itself to at
 * 60rem are gone, because the width was never ours to spend: D-15 says reps are on phones, and inside a CRM
 * this is whatever pane the host allocated rather than a page. `Tabs` mounts only the selected panel, which
 * is the saving the change is for - the widget's frame is sized to its content by `iqz-ddds:resize`, so a
 * panel that merely exists costs the host real pixels. What is decided here is which panel each tab shows,
 * what its count counts, and when its change marker goes up.
 *
 * The send queue renders below the tabs and outside them, deliberately. A progress bar unmounted by a tab
 * click would be DG.22 in a new place: a transfer that is still running, made invisible to the one person
 * who would conclude it had stopped.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  activitySchema,
  contactSchema,
  documentListSchema,
  documentRequestListSchema,
  documentRequestSchema,
  exchangeSchema,
  reminderSchema,
  type Contact,
  type DocumentRequestItem,
  type DocumentSummary,
  type Exchange,
  type ResolvedActivityEvent,
} from '@iqz-ddds/contracts';
import { ApiError, OfflineError, SessionLapsedError, type ApiClient, type UploadHooks } from './client';
import { ActivityPanel, ChecklistPanel, ExchangeHeader, FailureNotice, ReceivedPanel, SendQueue, SentPanel } from './panels';
import type { QueuedSend } from './panels';
import { RequestDocumentsDialog, SendDocumentsDialog } from './dialogs';
import { Tabs, type TabItem } from '../Tabs';
import type { UploadHandle } from '../upload/tus';


/**
 * What went wrong, and anything extra worth saying about it.
 *
 * `note` exists for a batch. "That label is too long" is true and useless on its own when two of three
 * labels were committed before it: a rep who cannot tell what landed will retype all three.
 */
interface Failure {
  readonly detail: string;
  readonly reference?: string;
  readonly note?: string;
}

interface Loaded {
  readonly exchange: Exchange;
  readonly contact: Contact | undefined;
  readonly requests: readonly DocumentRequestItem[];
  readonly documents: readonly DocumentSummary[];
  readonly activity: readonly ResolvedActivityEvent[];
  /**
   * Whose exchange this is, from the activity envelope rather than from a request of its own.
   *
   * It is on that envelope because `guest.grant_issued` has the *service* as its actor since DG.35 - the
   * dispatcher minted the grant on the strength of a decision the rep had already made - so the trail has
   * to say the send was automatic and still let a reviewer find whose instruction caused it. Naming the
   * custodian in the actor position would file a decision against somebody who did not make it in that
   * transaction, so it is one fact about the exchange and lives beside the events rather than in them.
   */
  readonly custodianName: string | null;
}

/**
 * The four tabs, in the order a rep meets them.
 *
 * The list is here as well as in the type because the marker diff walks it, and a tab that existed in the
 * union and not in this array would silently never be marked.
 */
const TAB_IDS = ['requested', 'received', 'sent', 'activity'] as const;

type TabId = (typeof TAB_IDS)[number];

/**
 * How much of the trail to ask for, which is as much as section 13 will give.
 *
 * `/activity` pages since DG.23, and its own default is 50. Asking for the maximum rather than taking that
 * is deliberate: R3 appends an event for every state change and R4 removes none, so a busy exchange passes
 * 50 in a fortnight, and D-14 makes this trail the primary compliance control rather than a convenience.
 * A rep reading it under pressure should not silently be shown a fortnight.
 *
 * It is still a page, so the panel says when it is one. A full page means there are older events; it does
 * not prove how many, and nothing here should pretend to know.
 */
const ACTIVITY_LIMIT = 200;

/**
 * How often the panel re-reads itself when nobody has said. DG.24.
 *
 * Short enough that a rep watching the screen while a prospect uploads sees the document arrive rather than
 * reloading to look for it, and long enough that five requests a tick is not load anybody has to think
 * about. A host with an opinion sends `pollSeconds`, and zero turns it off entirely.
 */
const DEFAULT_POLL_SECONDS = 15;

/** What each tab's contents amount to, one string per tab. See `poll`. */
type Fingerprint = Readonly<Record<TabId, string>>;

/**
 * No tab is marked, as one value.
 *
 * Shared rather than allocated per reset, so clearing markers that are already clear is an identity React
 * bails out of instead of a re-render of the whole view. Never mutated; the state's type says as much.
 */
const NO_MARKERS: ReadonlySet<TabId> = new Set();

/**
 * Reduce what is loaded to one string per tab, so a poll can tell which tabs moved.
 *
 * Ids alone are not enough, and the two extra fields are the whole point of DG.24: a row that said
 * "Checking" gaining a `currentVersionId` is the commit a rep is waiting for, and a second version arriving
 * on a document they already have is an arrival with an id they have already seen. `removedAt` is here for
 * the same reason in the other direction - R4 keeps the row, so a removal changes nothing else about it.
 *
 * A request's state is included because a fulfilled item leaves the checklist, which is a change to the
 * list the `Requested` count labels.
 *
 * The trail gets its length and its newest sequence rather than every row. It is append-only, so those two
 * together move exactly when something has been appended and never otherwise - and it is unbounded, so
 * joining every event into a string would grow without limit for no more information.
 */
function fingerprint(loaded: Loaded): Fingerprint {
  const documents = (origin: DocumentSummary['origin']): string =>
    loaded.documents
      .filter((document) => document.origin === origin)
      .map((document) => `${document.id}:${document.currentVersionId ?? ''}:${document.removedAt ?? ''}`)
      .join(',');

  const newest = loaded.activity.reduce((high, event) => Math.max(high, event.seq), 0);

  return {
    requested: loaded.requests.map((request) => `${request.id}:${request.state}`).join(','),
    received: documents('contact'),
    sent: documents('rep'),
    activity: `${String(loaded.activity.length)}:${String(newest)}`,
  };
}

/**
 * A count worth drawing, or nothing at all.
 *
 * Spread rather than passed, because `TabItem` declares `count?: number` and `exactOptionalPropertyTypes`
 * makes an absent property and one set to undefined different types. Zero is omitted rather than drawn: a
 * `0` badge still reads as a badge, which is the opposite of what it says.
 */
function badge(count: number): { count?: number } {
  return count === 0 ? {} : { count };
}

export interface ExchangeViewProps {
  readonly client: ApiClient;
  readonly exchangeId: string;
  /**
   * Reported to the host so a failure inside an iframe is visible in the CRM's own telemetry.
   *
   * Optional, because the standalone application has no host: an error there is already on the page the
   * representative is looking at, and there is nobody to tell.
   */
  readonly onError?: (code: string, reference?: string) => void;
  /** The container. A panel and a page want different geometry; see `.ddds-embedded`. */
  readonly className?: string;
  /**
   * Whether this view draws its own header - the contact's name and the two primary actions.
   *
   * `none` is for a host that already shows the person at the top of its own record and has its own
   * buttons: ours underneath theirs would be the same name twice and four controls for two intentions.
   * Everything else is unchanged, including both dialogs, which is what makes `onActions` able to open
   * exactly what our own header would have.
   */
  readonly chrome?: 'full' | 'none';
  /**
   * Where a dialog is drawn. DG.33, and a geometry constraint rather than a preference.
   *
   * The widget's frame is sized to its own content by `iqz-ddds:resize`, so its viewport *is* the whole
   * frame: a centred `position: fixed` dialog in a 1200px frame lands 600px down, below the fold of
   * whatever the CRM is showing. `takeover` hides the panels and puts the form in their place, so the
   * frame resizes to the form and the dialog is always inside what the rep can see. A page with a real
   * viewport gets a real overlay, which is the default.
   */
  readonly presentation?: 'overlay' | 'takeover';
  /**
   * Hands the two actions out, for a container that draws its own controls.
   *
   * Called once, after mount. The functions are stable and safe to hold: each opens one of the two
   * dialogs and does nothing else, so a host calling them gets exactly what its own button should have
   * done.
   */
  readonly onActions?: (actions: ExchangeActions) => void;
  /**
   * Which way the tab strip runs. DG.41, and a preference rather than a guarantee - see `Tabs`.
   *
   * A local union rather than the embed contract's, which is the precedent `chrome` above set: nothing in
   * this package knows the `postMessage` protocol exists, and that is what lets the widget's contract move
   * without this component moving with it. A mismatch between the two then fails to compile at the widget's
   * call site, which is where the coordinated release already lives.
   */
  readonly tabs?: 'horizontal' | 'vertical' | undefined;
  /**
   * Which tab to open on when this exchange is first shown.
   *
   * Honoured once per exchange and deliberately not afterwards. `docs/self-hosted/widget-integration.md` tells a host to
   * repeat every field on every re-post and names a theme change as a reason to re-post, so a view that
   * re-read this on every render would drag a rep from the trail back to the checklist because the CRM
   * switched to dark mode. See the reset keyed on `exchangeId` below.
   */
  readonly view?: TabId | undefined;
  /**
   * How often to re-read the exchange, in seconds. DG.24. Zero turns polling off.
   *
   * A customer's number rather than a compiled-in one: an embedded panel left open all day beside a CRM
   * record is a different load profile from the standalone application, and an instance that would rather
   * pay nothing for it says zero.
   */
  readonly pollSeconds?: number | undefined;
  /** Maximum length for an authored formatted document request. */
  readonly documentRequestMaxLength?: number | undefined;
}

export interface ExchangeActions {
  /** Ask this contact for documents. Opens the request dialog. */
  request(): void;
  /**
   * Send them documents. Opens the send dialog.
   *
   * It opens a dialog rather than a file picker, and that is the fix for a real defect rather than a
   * presentation choice: a picker opens only under transient user activation, a `message` handler has
   * none, and `postMessage` transfers none - so a host that called this when it clicked a hidden input
   * got nothing at all, silently. The rep's click on a control inside the dialog carries the activation
   * the host's message could not.
   */
  send(): void;
}

/**
 * The two callbacks an upload reports progress through, bound to one row of the queue.
 *
 * A factory rather than object literals at the call site, because inline they sit five function
 * definitions deep - component, callback, async body, hook, state updater - and at that depth the shape
 * of the code stops carrying its meaning. Out here each half is one line and reads as what it is.
 */
function sendingHooks(key: string, update: (key: string, change: Partial<QueuedSend>) => void): UploadHooks {
  return {
    onProgress: ({ sentBytes, totalBytes }) => update(key, { sentBytes, totalBytes }),
    onChecking: () => update(key, { stage: 'checking' }),
  };
}

/**
 * How far a batch of requests got before it was refused.
 *
 * Nothing to add for a single label: the refusal already says everything, and "0 of 1 was added" is a
 * sentence nobody needs. For a batch it is the difference between retyping one label and retyping three,
 * which the API's own message cannot know.
 */
function partialNote(added: number, total: number): string | undefined {
  if (total === 1) return undefined;
  const verb = added === 1 ? 'was' : 'were';
  return `${String(added)} of ${String(total)} ${verb} added; the rest were not.`;
}

/**
 * What to say once the pipeline has finished with a sent document.
 *
 * `committed` says nothing: the document is in the list the representative is looking at, which is the
 * confirmation. The other two do, and on this side of the exchange silence would be worse than on the
 * prospect's - a rep who believes a document was shared will tell their client so.
 */
function outcomeOf(handle: UploadHandle, filename: string): string | null {
  const because = handle.detail === null || handle.detail === '' ? '' : ` (${handle.detail})`;

  if (handle.state === 'rejected') {
    return `${filename} could not be accepted${because}. Nothing was saved, and the contact has not seen it.`;
  }
  if (handle.state === 'held') {
    return `${filename} needs a closer look before it can be added${because}. Nobody has to send it again.`;
  }
  if (handle.stillChecking) {
    return `${filename} was sent and is still being checked. It will appear here once it has been.`;
  }
  return null;
}

export function ExchangeView({
  client,
  exchangeId,
  onError,
  className = 'ddds-page',
  chrome = 'full',
  presentation = 'overlay',
  onActions,
  tabs,
  view,
  pollSeconds = DEFAULT_POLL_SECONDS,
  documentRequestMaxLength,
}: ExchangeViewProps): React.JSX.Element {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<Failure | undefined>(undefined);
  /**
   * Which writes are in flight, by name. DG.22.
   *
   * One boolean until 2026-08-05, and it was read by two panels at once - so asking for a
   * document put the other write's button into its "Sending" state while nothing was being
   * sent, to the one person who would act on that and tell their client to expect a
   * message. A set rather than a second boolean because the shape is what was wrong: this
   * view had three writes, has four now, and every one of them would have needed its own
   * flag threaded to its own panel. Two of the four now share one panel - the checklist and
   * its reminder - which makes the distinction easier to lose and worth a regression test.
   *
   * `sending` below stays separate even so. An upload has progress and an outcome, which
   * is more than "in flight or not".
   */
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(() => new Set());
  const [busyDocument, setBusyDocument] = useState<string | undefined>(undefined);
  /**
   * Which exchange a reminder has been accepted for, if any.
   *
   * The shape `sentTo` had on the invitation card that DG.18a deleted: held here rather than in the panel,
   * set on success only, and read back as a prop - the panels take data and callbacks and own no client.
   *
   * An exchange id rather than a boolean, and that is not defensive: the standalone application renders one
   * `ExchangeView` and swaps `exchangeId` under it rather than remounting per contact, so a boolean would
   * survive the navigation and tell the next contact's panel a message was on its way to them. Naming the
   * exchange the press was made on makes that impossible rather than unlikely.
   *
   * It is never cleared on a timer. Nothing about it stops being true, and restoring the button after a
   * minute would invite exactly the second press the deduplication window absorbs - see `remind`.
   */
  const [remindedFor, setRemindedFor] = useState<string | undefined>(undefined);
  /**
   * The batch being sent, and what became of the last one.
   *
   * Its own state rather than the shared `inFlight` set, deliberately. That set is read by more than one
   * control, so an upload joining it would make the checklist's reminder announce "Reminding" while a
   * document was being transferred - a thing that is not happening, said to the one person who would
   * believe it.
   *
   * A list rather than one entry since DG.33: a rep picks several files in the dialog and they are
   * uploaded one at a time, so the ones still queued need a row of their own or they look dropped.
   */
  const [queue, setQueue] = useState<readonly QueuedSend[]>([]);

  /**
   * Which dialog is open, if either. DG.33.
   *
   * One piece of state rather than two booleans, because two booleans can both be true and there is no
   * meaning for that. Both verbs used to act on the panel directly - one focused a field, the other
   * clicked a hidden file input - and the second could never work from a host's `postMessage`, which
   * carries no user activation for a file picker to spend.
   */
  const [dialog, setDialog] = useState<'request' | 'send' | null>(null);

  /** Which of the four tabs is open. Seeded from the host's `view` and owned here afterwards. */
  const [tab, setTab] = useState<TabId>(view ?? 'requested');
  /**
   * Which tabs have changed without this rep doing it. DG.24.
   *
   * "Without you" is narrower than "changed", and it is the only reading that works: R3 appends an audit
   * event for every state change, so a marker raised by diffing what was loaded would light the trail up
   * every time the rep withdrew a request - a notification about their own click, on the tab they are least
   * likely to want interrupted. So only `poll` ever adds to this set, `load` never does, and opening a tab
   * takes it back out.
   */
  const [changed, setChanged] = useState<ReadonlySet<TabId>>(NO_MARKERS);

  /** Distinguishes one batch's rows from the next one's, so React does not reuse them. */
  const batch = useRef(0);
  /** Which reminder this is, for the idempotency key. See `remind`. */
  const attempt = useRef(0);
  /** Files waiting their turn, and whether anything is already working through them. See `send`. */
  const queued = useRef<{ file: File; key: string }[]>([]);
  const draining = useRef(false);

  /**
   * What the tabs looked like the last time this rep can be said to have seen them.
   *
   * Undefined until the first read lands, which is what makes the first poll of a freshly loaded exchange
   * mark nothing: there is no "before" to have differed from.
   */
  const baseline = useRef<Fingerprint | undefined>(undefined);
  /**
   * Bumped by every `load`, so a poll can tell that its answer has been overtaken.
   *
   * The race it closes: a poll's five requests go out, the rep sends a document, the write's reload lands,
   * and then the poll's pre-send answer arrives and puts the document list back the way it was. A rep
   * watching for exactly that document would see it appear and vanish.
   */
  const generation = useRef(0);
  /** Which exchange the tab and the markers belong to. See the reset below. */
  const shownFor = useRef(exchangeId);

  /*
   * Start this exchange fresh, because the standalone application swaps `exchangeId` under one mounted view
   * rather than remounting per contact - the hazard `remindedFor` above already records.
   *
   * Without it the next contact's panel opens on the tab the previous rep left, wearing the previous
   * exchange's markers, and the first poll diffs exchange B against exchange A's baseline and marks
   * everything as new.
   *
   * Keyed on `exchangeId` alone and never on `view`, which matters: section 14 tells a host to repeat every
   * field on every re-post and names a theme change as a reason to re-post, so keying on `view` as well
   * would move a rep who is reading the trail back to the checklist because the CRM went dark. `view` is
   * therefore an opening position rather than a setting.
   *
   * Adjusted during render rather than in an effect. React re-runs this component before committing
   * anything, so nobody ever sees the frame where the tab belongs to the previous contact.
   */
  if (shownFor.current !== exchangeId) {
    shownFor.current = exchangeId;
    setTab(view ?? 'requested');
    setChanged(NO_MARKERS);
    baseline.current = undefined;
  }

  /** A transfer is running. The header's own condition, unchanged: an upload is not a checklist write. */
  const sending = queue.some((entry) => entry.stage !== 'done');
  /**
   * A write of this rep's is running, in either of the two shapes it takes.
   *
   * Wider than the header's condition on purpose, and read only by the poll: a poll landing mid-transfer, or
   * between a withdrawal being accepted and its reload arriving, moves the list under somebody who is
   * watching it. Both sets have to be quiet, not just one.
   */
  const writing = inFlight.size > 0 || sending;

  /*
   * What the poll needs to know, through refs assigned during render - the `closeRef` idiom in `Modal`.
   *
   * The alternative is a poll callback that changes identity whenever any of these does, which would tear
   * the interval down and rebuild it. `queue` is the one that makes that fatal rather than untidy: it
   * changes on every progress event, so the interval would be re-created several times a second during an
   * upload and its phase reset with it, and it would therefore never fire at all while a large file was
   * being sent - the exact window in which a rep is most likely to be watching.
   */
  const busyRef = useRef(writing);
  busyRef.current = writing;
  const tabRef = useRef(tab);
  tabRef.current = tab;

  /** Change one row of the queue, leaving the rest of the batch alone. */
  const update = useCallback((key: string, change: Partial<QueuedSend>): void => {
    setQueue((current) => current.map((entry) => (entry.key === key ? { ...entry, ...change } : entry)));
  }, []);

  /**
   * Open a tab, and stop telling the rep it has changed - they are looking at it.
   *
   * The same set identity is returned when there is nothing to clear, so the common case - a click on a tab
   * nobody had marked - costs one state update for the selection and no re-render for the markers.
   */
  const openTab = useCallback((next: TabId) => {
    setTab(next);
    setChanged((current) => {
      if (!current.has(next)) return current;
      const remaining = new Set(current);
      remaining.delete(next);
      return remaining;
    });
  }, []);

  const askForDocument = useCallback(() => setDialog('request'), []);
  const openSend = useCallback(() => setDialog('send'), []);
  const closeDialog = useCallback(() => setDialog(null), []);

  /*
   * Hand the two actions to whoever is containing this, once.
   *
   * `onActions` is deliberately absent from the dependency list beyond its own identity: a host that
   * passes an inline arrow would otherwise re-register on every render, which is harmless but makes the
   * effect look like it does more than it does. The two callbacks are stable, so one call is enough.
   */
  useEffect(() => {
    onActions?.({ request: askForDocument, send: openSend });
  }, [onActions, askForDocument, openSend]);

  const report = useCallback(
    (err: unknown, code: string, note?: string) => {
      const extra = note === undefined ? {} : { note };
      if (err instanceof ApiError) {
        setFailure({
          detail: err.detail,
          ...(err.reference === undefined ? {} : { reference: err.reference }),
          ...extra,
        });
        onError?.(code, err.reference);
        return;
      }
      if (err instanceof SessionLapsedError || err instanceof OfflineError) {
        setFailure({ detail: err.message, ...extra });
        onError?.(err instanceof SessionLapsedError ? 'session_lapsed' : 'offline');
        return;
      }
      setFailure({ detail: 'Something went wrong here.', ...extra });
      onError?.(code);
    },
    [onError],
  );

  /**
   * Everything the panel shows, in one pass, and no state of its own.
   *
   * Five requests rather than one aggregate endpoint, because section 13 has no aggregate and inventing one
   * would be a sixth documented addition to that list for a saving nobody has measured. They run concurrently,
   * and the contact is fetched separately because the exchange projection carries `contactId` and no name.
   *
   * Split out from `load` for DG.24 rather than for tidiness. `load` clears the failure when it succeeds,
   * which is right for a reload the rep asked for and wrong for a poll: a successful poll would silently
   * erase the notice a refused withdrawal had raised a few seconds earlier, and the rep would be left
   * believing the withdrawal took. Two callers, one read, and only one of them touches the notice.
   */
  const read = useCallback(async (): Promise<Loaded> => {
    const exchange = await client.get(`/api/v1/exchanges/${exchangeId}`, exchangeSchema);

    const [requests, documents, activity, contact] = await Promise.all([
      client.get(`/api/v1/exchanges/${exchangeId}/requests`, documentRequestListSchema),
      client.get(`/api/v1/exchanges/${exchangeId}/documents`, documentListSchema),
      client.get(`/api/v1/exchanges/${exchangeId}/activity?limit=${String(ACTIVITY_LIMIT)}`, activitySchema),
      // A scoped token may read a contact - the same rep can read the whole directory with a provider
      // bearer - so this is not a privilege the widget was given specially.
      client.get(`/api/v1/contacts/${exchange.contactId}`, contactSchema).catch(() => undefined),
    ]);

    return {
      exchange,
      contact,
      requests: requests.requests,
      documents: documents.documents,
      activity: activity.events,
      custodianName: activity.custodianName,
    };
  }, [client, exchangeId]);

  /**
   * The read a rep asked for: on mount, and after anything they did themselves.
   *
   * It sets the baseline and raises no marker, which is the rule the change dot lives or dies by. Every
   * write appends an audit event, so a reload that diffed itself against the previous one would mark the
   * trail after every withdrawal - a notification about the rep's own click.
   */
  const load = useCallback(async () => {
    /*
     * Bumped before the read rather than after it, so a poll already in flight is invalidated the moment a
     * write's reload begins rather than only once it lands. The window between the two is exactly where the
     * stale answer would otherwise arrive.
     */
    generation.current += 1;
    try {
      const next = await read();
      baseline.current = fingerprint(next);
      setLoaded(next);
      setFailure(undefined);
    } catch (err) {
      report(err, 'load_failed');
    }
  }, [read, report]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * `read` through a ref, so `poll` below has no dependencies and the interval is built once.
   *
   * This stands in for `client` and `exchangeId`, which are what `read` closes over. An exchange swapped
   * under a live interval is handled by the reset above rather than by rebuilding the timer: the next tick
   * reads the new exchange, and the baseline it diffs against was cleared, so it marks nothing.
   */
  const readRef = useRef(read);
  readRef.current = read;

  /**
   * The read nobody asked for. DG.24.
   *
   * A rep watching the screen while a prospect uploads used to see nothing at all until they reloaded,
   * because this view read once on mount and then only after something it had done itself. What is
   * deliberate here is everything it does *not* do.
   *
   * It raises no notice and tells the host nothing. Nobody asked for this read, so a failure banner would
   * appear for something the rep did not do and could not act on, and `report` would fire `onError` into the
   * host CRM's telemetry once every interval for as long as a flaky connection lasted - turning one rep's
   * bad wifi into an incident. The cost is a panel that can be quietly stale, and that is accepted rather
   * than unnoticed: the rep's next action reloads through `load`, which does report.
   *
   * It also leaves the failure alone on success, which is why `read` exists separately - see above.
   */
  const poll = useCallback(async () => {
    const stamp = generation.current;
    try {
      const next = await readRef.current();
      // Overtaken by a write's reload while this was in flight. That answer is newer, whatever this one says.
      if (generation.current !== stamp) return;

      const before = baseline.current;
      baseline.current = fingerprint(next);
      setLoaded(next);

      // Nothing has been seen yet, so nothing can have changed without being seen. The first poll after a
      // load or after an exchange swap marks nothing for this reason.
      if (before === undefined) return;

      const after = baseline.current;
      // Not the open tab: the rep is looking at it, and the panel has just been re-rendered with what
      // arrived. A dot on the tab you are reading is a notification about the screen in front of you.
      const moved = TAB_IDS.filter((id) => id !== tabRef.current && after[id] !== before[id]);
      if (moved.length === 0) return;

      setChanged((current) => {
        const marked = new Set(current);
        for (const id of moved) marked.add(id);
        return marked;
      });
    } catch {
      /*
       * Swallowed, and this is the point rather than an omission. See the doc comment above: a notice here
       * would be about a read the rep did not ask for, and reporting it would put one rep's connection into
       * the host's error stream every interval. The interval is not cleared either - a poll that failed says
       * nothing about the next one, and giving up would strand the panel silently for the rest of the day.
       */
    }
  }, []);

  /*
   * The interval, and the two conditions under which it does nothing.
   *
   * Hidden first: a forgotten tab left open overnight would otherwise re-read the exchange every few
   * seconds until morning, for a screen nobody is looking at. And busy second: see `busyRef`.
   *
   * The `visibilitychange` listener reads immediately rather than waiting the interval out. A rep who
   * switched back to the CRM did so because they want to know now, and making them wait up to fifteen
   * seconds for a panel that is already on screen is the stale read this whole mechanism exists to remove.
   */
  useEffect(() => {
    // Zero is off, and off means no timer rather than a timer that returns early. A host that pays nothing
    // for this should have nothing scheduled.
    if (pollSeconds <= 0) return undefined;

    const tick = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (busyRef.current) return;
      void poll();
    };

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') tick();
    };

    const timer = setInterval(tick, pollSeconds * 1000);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [poll, pollSeconds]);

  /**
   * Run one write, marking just that operation busy.
   *
   * `key` names the operation rather than the panel, so two panels reading the same key
   * is a deliberate choice at the call site rather than an accident of sharing one flag.
   *
   * `note` is read only if the work throws, and exists for a batch: an error saying "that label is too
   * long" is true and useless on its own when two of three labels were committed before it.
   */
  const mutate = useCallback(
    async (key: string, work: () => Promise<void>, code: string, note?: () => string | undefined) => {
      setInFlight((current) => new Set(current).add(key));
      try {
        await work();
        await load();
      } catch (err) {
        /*
         * Reload first, then say what went wrong. Both halves matter and the order is not incidental.
         *
         * A failed write may still have changed the server - a batch of three labels that was refused on
         * the second one committed the first - so the panel has to be re-read or it shows a state that
         * never existed. And `load` clears the failure on success, so reporting before it would put a
         * notice on the screen and immediately take it away again.
         */
        await load();
        report(err, code, note?.());
      } finally {
        setInFlight((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    },
    [load, report],
  );

  /**
  * Ask for one formatted document request. DG.33.
   *
   * Sequential rather than concurrent, and it stops at the first refusal. Each of these appends an audit
   * event and bumps the exchange version, so the order a rep asked in is the order the trail records -
   * and a batch is usually wrong as a batch, so posting the remaining four after the first was refused
   * would mostly produce four more refusals and one confusing notice.
   *
   * Emits `document.requested`, once per label.
   */
  const requestDocuments = useCallback(
    (label: string) => {
      let added = 0;
      void mutate(
        'checklist',
        async () => {
          // The created item comes back and is parsed, even though the panel reloads everything a moment
          // later: a response that does not match the contract is a defect worth one error here rather
          // than a silently absent row after the reload.
          await client.post(`/api/v1/exchanges/${exchangeId}/requests`, { label }, documentRequestSchema);
          added += 1;
        },
        'request_failed',
        () => partialNote(added, 1),
      );
    },
    [client, exchangeId, mutate],
  );

  const withdrawRequest = useCallback(
    (requestId: string) => {
      // The same key as adding, deliberately: both change the checklist, and a panel that
      // let you withdraw an item while another was being added would reload under itself.
      void mutate('checklist', async () => {
        await client.delete(`/api/v1/exchanges/${exchangeId}/requests/${requestId}`);
      }, 'withdraw_failed');
    },
    [client, exchangeId, mutate],
  );

  /**
   * Nudge a contact who was already told.
   *
   * There is no manual introduction to send any more: asking for a document or sending one emails them,
   * and for somebody who has never signed in that message carries the link. So this covers the one case
   * the automatic message does not - a person who was told and has not acted - and mints nothing the
   * ordinary path would not have minted anyway.
   *
   * 202 with a body saying which reading we took, `requested` or `shared`. Neither word reaches the screen:
   * the trail below records which message went, and the footer's job is to say that a message is coming at
   * all rather than to describe it.
   *
   * ## Why a press has to leave something behind
   *
   * The two automatic notifications now schedule a wake for when their coalesced message becomes due. The
   * reminder deliberately does not, and that is settled: its outbox row is due immediately and its
   * one-minute idempotency key is a *deduplication* window rather than a delay, so waking the dispatcher
   * early would deliver the first press inside that window, free the pending key, and turn a second press
   * twenty seconds later into a second email - reversing "two presses, one message". So it waits for the
   * once-a-minute `outbox.dispatch` cron, and up to about a minute of silence is correct rather than slow.
   *
   * That minute used to look like a press that did not take, so the panel now says what happened and stops
   * offering the button. What it may not say is "sent": at the moment this resolves the message has been
   * accepted for delivery and nothing more, which is why the endpoint is a 202 and why its body carries no
   * `delivered` flag. The copy is in `ChecklistPanel`.
   */
  const remind = useCallback(() => {
    void mutate('remind', async () => {
      await client.post(`/api/v1/exchanges/${exchangeId}/reminders`, {}, reminderSchema, {
        idempotencyKey: `remind:${exchangeId}:${String(attempt.current)}`,
      });
      /*
       * Rotated on success only, which is what makes the key do its job in both directions.
       *
       * Section 15 replays a repeat within twenty-four hours, so a second press of the same key is free
       * where a fresh one would be a second email. A key that never changed would make tomorrow's
       * deliberate reminder a replay of today's; one that changed per press would make a retry after a
       * failed *response* send a message that may already have gone.
       */
      attempt.current += 1;
      // Inside the work, so a refusal leaves the button offered: the remedy for a 429 or a 409 is to retry
      // or to wait, and neither is helped by replacing the control with a sentence about a queued message.
      setRemindedFor(exchangeId);
    }, 'remind_failed');
  }, [client, exchangeId, mutate]);

  /**
   * Ask the API for the content, then let the browser have it.
   *
   * The asking is scripted, because the token is a header rather than a query parameter (section 14 forbids
   * one in a URL) and a plain link cannot carry it. What comes back is either a signed URL or the bytes.
   *
   * Both end at an anchor, and for the URL that is not a convenience - it is the fix. This panel is on its
   * own origin, so a scripted request to the storage host is CORS-checked against a bucket that cannot be
   * configured to allow it: after a redirect the browser sends `Origin: null` rather than ours. Clicking an
   * anchor is a navigation, which is not CORS-checked, and the repository's own `Content-Disposition` makes
   * it a download rather than a page. `anchor.download` is ignored for a cross-origin href, which is why the
   * filename has to come from that header and not from here.
   */
  const download = useCallback(
    (document_: DocumentSummary) => {
      setBusyDocument(document_.id);
      void (async () => {
        try {
          const content = await client.download(`/api/v1/documents/${document_.id}/content`);
          const href = content.kind === 'url' ? content.url : URL.createObjectURL(content.blob);
          const anchor = document.createElement('a');
          anchor.href = href;
          anchor.download = document_.name;
          anchor.click();
          // Revoked immediately: a blob URL holds the whole file in memory until it is, and a rep opening
          // several documents in a session would otherwise accumulate all of them. There is nothing to
          // revoke on the other branch, because the bytes never entered this tab.
          if (content.kind === 'blob') URL.revokeObjectURL(href);
        } catch (err) {
          report(err, 'download_failed');
        } finally {
          setBusyDocument(undefined);
        }
      })();
    },
    [client, report],
  );

  /**
   * Send one or several documents to the contact. DG.1, batched by DG.33.
   *
   * One at a time. Concurrent transfers of a 350MB scan and a 40KB form would starve each other on a
   * rep's connection, and the resumable protocol's offsets are per-upload anyway - so the queue is a
   * queue rather than a `Promise.all` with a progress bar over it.
   *
   * The wait is named rather than left blank, and the re-read happens after each file rather than after
   * the batch: R5 puts a scan between the last chunk and the system of record, so reading the document
   * list when the commit returns renders the state from before that upload existed. That is the mistake
   * DG.14 fixed on the prospect's side and it would be exactly as easy to make again here.
   *
   * Unlike the checklist, a refusal does not stop the rest. Each file is its own transfer, and one being
   * too large or infected says nothing about the next.
   */
  const send = useCallback(
    (files: readonly File[]) => {
      batch.current += 1;
      const stamp = batch.current;
      const keyed = files.map((file, index) => ({ file, key: `${String(stamp)}-${String(index)}` }));

      setQueue((current) => [
        // Finished rows go, so a rep does not accumulate a column of old outcomes. Unfinished ones stay,
        // because a second batch started while the first is running joins it rather than replacing it.
        ...current.filter((entry) => entry.stage !== 'done'),
        ...keyed.map(({ file, key }) => ({
          key,
          filename: file.name,
          sentBytes: 0,
          totalBytes: file.size,
          stage: 'waiting' as const,
          outcome: null,
        })),
      ]);

      queued.current.push(...keyed);

      /*
       * One drain loop, however many times this is called.
       *
       * A host with `chrome: 'none'` draws its own button and nothing disables it, so a rep can start a
       * second batch while the first is still transferring. Two loops would upload concurrently, which
       * is what the queue exists to prevent, and would fight over the same rows.
       */
      if (draining.current) return;
      draining.current = true;

      void (async () => {
        try {
          for (;;) {
            const next = queued.current.shift();
            if (next === undefined) return;

            update(next.key, { stage: 'sending' });
            try {
              const handle = await client.upload(exchangeId, next.file, sendingHooks(next.key, update));
              await load();
              update(next.key, { stage: 'done', outcome: outcomeOf(handle, next.file.name) });
            } catch (err) {
              // The rest of the batch carries on. Each file is its own transfer, and one being too large
              // says nothing about the next - unlike the checklist, where a batch is usually wrong as one.
              report(err, 'upload_failed');
              update(next.key, { stage: 'done', outcome: null });
            }
          }
        } finally {
          draining.current = false;
        }
      })();
    },
    [client, exchangeId, load, report, update],
  );

  if (loaded === undefined) {
    return (
      <div className={className}>
        {failure === undefined ? (
          <p className="ddds-empty">Loading.</p>
        ) : (
          <FailureNotice detail={failure.detail} reference={failure.reference} />
        )}
      </div>
    );
  }

  /*
   * The panel gives way to a takeover, and goes unreachable behind an overlay.
   *
   * `hidden` rather than unmounting, so an upload in flight and everything already fetched survive a
   * dialog being opened and closed over them. `inert` is the overlay's half of the same idea: without it
   * a Tab out of the dialog walks into a panel the rep cannot see they have reached, and a screen reader
   * reads both at once.
   */
  const takenOver = presentation === 'takeover' && dialog !== null;
  const behindOverlay = presentation === 'overlay' && dialog !== null;

  /*
   * Whether there is anything to remind about, from what is already on the screen.
   *
   * The two readings the endpoint reports back as `sent`: something still awaited, or something waiting
   * to be read. Derived rather than fetched, because both halves are already loaded and a request whose
   * only purpose is to decide whether a button is grey is a request nobody should pay for. The endpoint
   * remains the control - it answers 409 - and this is only about not offering a press that cannot work.
   *
   * The second half has to match `chooseReminder` on the server exactly, and "any document" does not:
   * reminding somebody about a document *they* sent us is not a reminder, and a document whose versions
   * are all removed is nothing to read. Counting either here would offer a press that comes back 409,
   * which is the one outcome this derivation exists to avoid.
   */
  const canRemind =
    loaded.requests.some((request) => request.state === 'awaiting') ||
    loaded.documents.some((document) => document.origin === 'rep' && document.currentVersionId !== null);

  /*
   * The strip, from what is already loaded. No sixth request for three numbers.
   *
   * Two rules about those numbers are worth a sentence each. The outstanding count uses the same predicate
   * `ChecklistPanel` filters its rows on, because a count that disagrees with the list it labels is worse
   * than no count at all - a rep who reads "2" and sees one row has to work out which of the two lied.
   *
   * And removed documents *are* counted, for the same reason rather than in spite of it: R4 keeps their rows
   * in the list, wearing the date they were removed, so a count that excluded them would be the same
   * disagreement in the other direction.
   *
   * The trail gets no count. It is the one list here with no upper bound - R3 appends an event for every
   * state change and R4 never removes one - so a number on it would say how long the exchange has been open
   * and nothing a rep can act on.
   */
  const items: readonly TabItem<TabId>[] = [
    {
      id: 'requested',
      label: 'Requested',
      ...badge(loaded.requests.filter((request) => request.state === 'awaiting').length),
      changed: changed.has('requested'),
    },
    {
      id: 'received',
      label: 'Received',
      ...badge(loaded.documents.filter((document) => document.origin === 'contact').length),
      changed: changed.has('received'),
    },
    {
      id: 'sent',
      label: 'Sent',
      ...badge(loaded.documents.filter((document) => document.origin === 'rep').length),
      changed: changed.has('sent'),
    },
    { id: 'activity', label: 'Activity', changed: changed.has('activity') },
  ];

  return (
    <div className={className}>
      {/*
        Everything that is not the dialog, as one thing.

        The header is inside it rather than beside it so that both states cover it: a takeover has to
        hide it, and an overlay has to make it unreachable - a header left live behind a modal is a
        second "Send a document" a rep can press while the first dialog is open, and `aria-modal` has
        already told a screen reader nothing out here exists.
      */}
      <div className="ddds-rep-body" hidden={takenOver} inert={behindOverlay}>
        {/* The host draws its own when `chrome` is `none`; see ExchangeViewProps. */}
        {chrome === 'full' && (
          <ExchangeHeader
            exchange={loaded.exchange}
            contact={loaded.contact}
            onRequest={askForDocument}
            onSend={openSend}
            busy={sending}
          />
        )}

        {failure !== undefined && (
          <FailureNotice detail={failure.detail} reference={failure.reference}>
            {failure.note !== undefined && <span className="ddds-notice__reference">{failure.note}</span>}
          </FailureNotice>
        )}

        {/*
          Four tabs, one panel mounted. DG.41.

          `orientation` is spread rather than passed because `exactOptionalPropertyTypes` makes an absent
          prop and one set to undefined different types, and `Tabs` declares it without `| undefined` - it
          has its own default, which is the one that should win when the host has no opinion.
        */}
        <Tabs items={items} selected={tab} onSelect={openTab} label="Exchange" {...(tabs === undefined ? {} : { orientation: tabs })}>
          {tab === 'requested' && (
            <ChecklistPanel
              requests={loaded.requests}
              onWithdraw={withdrawRequest}
              pending={inFlight.has('checklist')}
              onRemind={remind}
              reminding={inFlight.has('remind')}
              canRemind={canRemind}
              reminded={remindedFor === exchangeId}
            />
          )}
          {/*
            Both lists take the whole set and filter it themselves, rather than being handed a slice. One
            list, one predicate, in the component that draws the list - which is also what makes the tab's
            count and its panel's rows unable to disagree about what "received" means.
          */}
          {tab === 'received' && <ReceivedPanel documents={loaded.documents} onDownload={download} busyId={busyDocument} />}
          {tab === 'sent' && <SentPanel documents={loaded.documents} onDownload={download} busyId={busyDocument} />}
          {tab === 'activity' && (
            <ActivityPanel
              events={loaded.activity}
              custodianName={loaded.custodianName}
              // A full page is the only evidence available that there are older events. It cannot say how
              // many, and the panel's wording is careful not to.
              truncated={loaded.activity.length >= ACTIVITY_LIMIT}
            />
          )}
        </Tabs>

        {/*
          Below the tabs and outside them, which is a decision rather than a layout accident.

          A transfer survives a tab click this way. Inside the `Sent` panel it would be unmounted the moment
          a rep looked at the checklist, and a 350MB upload with no visible progress reads as one that has
          stopped - DG.22 in a new place. It is inside `.ddds-rep-body` for the other half of the same
          reasoning: the `hidden` and `inert` above have to cover it, or a dialog opens over a live progress
          bar that a screen reader still reads out.

          `SendQueue` renders nothing when the queue is empty, so there is no wrapper to hide here.
        */}
        <SendQueue queue={queue} />
      </div>

      {dialog === 'request' && (
        <RequestDocumentsDialog
          variant={presentation}
          documentRequestMaxLength={documentRequestMaxLength}
          onClose={closeDialog}
          onSubmit={(label) => {
            // Closed before the work starts, both here and for sending: what a rep watches after this is
            // the panel, which is the one thing that says what the server has.
            closeDialog();
            requestDocuments(label);
          }}
        />
      )}

      {dialog === 'send' && (
        <SendDocumentsDialog
          variant={presentation}
          onClose={closeDialog}
          onSubmit={(files) => {
            closeDialog();
            send(files);
          }}
        />
      )}
    </div>
  );
}
